// /lib/chart-helpers/custom-overlays/interactiveSlTpLineOverlay.ts

import { klinecharts } from "../klinecharts-client";

// Import profit calculation functions
import { calculateProfitAtPrice } from "../calculateSLTPValues";

export interface InteractiveSlTpLineData {
  price: number;
  type: "SL" | "TP";
  tradeType?: "BUY" | "SELL";
  ticket?: string;
  openTime?: number; // Add timestamp for when trade was opened
  originalPrice?: number; // Add original trade SL/TP price for comparison
  currentTradeValue?: number; // Add current reactive value from trade for button logic
  autoSlTpModify?: boolean; // Add autoSlTpModify setting
  isFirstAddition?: boolean; // Flag to track if this is the first addition of new SL/TP
  // Add new fields for profit/pips calculation
  tradeData?: any; // Full trade data for profit calculation
  isPipsVisible?: boolean; // Whether to show pips or currency
}

// Global callback storage for overlay instances
const overlayCallbacks = new Map<
  string,
  {
    onBadgeClick?: (
      type: "SL" | "TP",
      action: "remove" | "modify",
      price: number
    ) => void;
    onAutoModify?: (type: "SL" | "TP", price: number) => void;
    getCurrentAutoSlTpModify?: () => boolean; // Callback to get current autoSlTpModify value
  }
>();

// Function to store callbacks for an overlay
const storeOverlayCallbacks = (
  overlayId: string,
  callbacks: {
    onBadgeClick?: (
      type: "SL" | "TP",
      action: "remove" | "modify",
      price: number
    ) => void;
    onAutoModify?: (type: "SL" | "TP", price: number) => void;
    getCurrentAutoSlTpModify?: () => boolean;
  }
) => {
  overlayCallbacks.set(overlayId, callbacks);
};

// Function to get callbacks for an overlay
const getOverlayCallbacks = (overlayId: string) => {
  return overlayCallbacks.get(overlayId);
};

// Function to remove callbacks for an overlay
const removeOverlayCallbacks = (overlayId: string) => {
  overlayCallbacks.delete(overlayId);
};

// Function to get line color based on SL/TP type
const getLineColor = (lineData: InteractiveSlTpLineData) => {
  if (lineData.type === "SL") {
    return "#F44336"; // Red for Stop Loss
  } else {
    return "#4CAF50"; // Green for Take Profit
  }
};

// Visual constants kept loosely in sync with `tradeLevelOverlay` so the
// "creating new SL/TP" line looks identical to the committed one.
const ACTION_BADGE_KEY = "actionBadge";
const LEFT_STATUS_KEY = "leftStatus";
const MIDDLE_LABEL_KEY = "middleLabel";
const RIGHT_LABEL_KEY = "rightLabel";
const ACTION_BUTTON_VISUAL_HALF_WIDTH = 10;
const LEFT_STATUS_GAP = 6;

const createInteractiveSlTpLineFigures = (params: any) => {
  const { coordinates, bounding, overlay, yAxis } = params;

  if (coordinates.length < 1) return [];

  const figures: any[] = [];
  const startPoint = coordinates[0];
  const lineData: InteractiveSlTpLineData = overlay.extendData;

  if (!lineData) return [];

  const lineColor = getLineColor(lineData);

  // Real-time price from yAxis conversion (drag follows the cursor).
  const currentPrice = yAxis
    ? yAxis.convertFromPixel(startPoint.y)
    : lineData.price;

  // Anchor the line at the trade's open time. `points[0].timestamp` is set
  // to `lineData.openTime` in `createInteractiveSlTpLineOverlay`, so
  // `coordinates[0].x` lands on the open-time pixel. Clamp into the visible
  // pane so a trade that opened off-screen still shows its drag pill.
  const lineStartX = Math.max(
    0,
    Math.min(startPoint.x ?? 0, bounding.width)
  );

  // Invisible ~16px-tall hit-test rect so the SL/TP line is easy to grab —
  // klinecharts' default 2px line deviation makes a bare line a needle-thread.
  figures.push({
    key: "interactiveSlTpHitArea",
    type: "rect",
    attrs: {
      x: lineStartX,
      y: startPoint.y - 8,
      width: Math.max(0, bounding.width - lineStartX),
      height: 16,
    },
    styles: {
      style: "fill",
      color: "rgba(0, 0, 0, 0)",
    },
    ignoreEvent: false,
  });

  figures.push({
    key: "interactiveSlTpLine",
    type: "line",
    attrs: {
      coordinates: [
        { x: lineStartX, y: startPoint.y },
        { x: bounding.width, y: startPoint.y },
      ],
    },
    styles: {
      style: "solid",
      color: lineColor,
      size: 2,
    },
  });

  const currentTradeValue = lineData.currentTradeValue || 0;
  const valuesAreEqual =
    Math.abs(currentPrice - currentTradeValue) < 0.00001;
  const badgeSymbol = valuesAreEqual ? "×" : ">";

  // Left action chip — `[X]` to remove (when the dragged price matches the
  // committed one) or `[>]` to commit the new value. Mirrors the unified
  // overlay's close button position and styling.
  figures.push({
    key: ACTION_BADGE_KEY,
    type: "text",
    attrs: {
      x: lineStartX,
      y: startPoint.y,
      text: badgeSymbol,
      align: "center",
      baseline: "middle",
    },
    styles: {
      style: "fill",
      color: "#FFFFFF",
      size: 11,
      family: "Arial",
      weight: "bold",
      backgroundColor: lineColor,
      paddingLeft: 5,
      paddingRight: 5,
      paddingTop: 2,
      paddingBottom: 2,
      borderRadius: 3,
      borderStyle: "solid",
      borderColor: lineColor,
      borderSize: 1,
      cursor: "pointer",
    },
    ignoreEvent: false,
  });

  // Projected P&L / pips at the dragged price — pinned next to the action
  // chip on the left, just like the committed overlay's left status pill.
  let profitDisplay = "";
  if (lineData.tradeData && lineData.tradeData.openPrice) {
    try {
      profitDisplay = calculateProfitAtPrice(
        {
          ...lineData.tradeData,
          digits: lineData.tradeData.digits,
          lots: lineData.tradeData.lots,
          openPrice: lineData.tradeData.openPrice,
          orderType: lineData.tradeData.orderType,
        },
        currentPrice,
        lineData.isPipsVisible || false
      );
    } catch (error) {
      console.error("Failed to calculate profit at price:", error);
      profitDisplay = lineData.isPipsVisible ? "0.0p" : "0.00";
    }
  }

  if (profitDisplay) {
    figures.push({
      key: LEFT_STATUS_KEY,
      type: "text",
      attrs: {
        x: lineStartX + ACTION_BUTTON_VISUAL_HALF_WIDTH + LEFT_STATUS_GAP,
        y: startPoint.y,
        text: profitDisplay,
        align: "left",
        baseline: "middle",
      },
      styles: getInteractivePillStyles(lineColor),
      ignoreEvent: true,
    });
  }

  // Middle "Take Profit" / "Stop Loss" label — purely visual; the entire
  // overlay is still draggable, so grabbing the chip drags the line.
  const middleX = (lineStartX + bounding.width) / 2;
  figures.push({
    key: MIDDLE_LABEL_KEY,
    type: "text",
    attrs: {
      x: middleX,
      y: startPoint.y,
      text: lineData.type === "TP" ? "Take Profit" : "Stop Loss",
      align: "center",
      baseline: "middle",
    },
    styles: getInteractivePillStyles(lineColor),
    ignoreEvent: false,
  });

  // Right pill — "TP/SL #ticket price" so the user sees the full label
  // exactly like the committed line.
  const priceText = currentPrice.toFixed(
    Math.max(0, Math.min(lineData.tradeData?.digits || 5, 8))
  );
  const rightText = lineData.ticket
    ? `${lineData.type} #${lineData.ticket} ${priceText}`
    : `${lineData.type} ${priceText}`;
  figures.push({
    key: RIGHT_LABEL_KEY,
    type: "text",
    attrs: {
      x: bounding.width - 4,
      y: startPoint.y,
      text: rightText,
      align: "right",
      baseline: "middle",
    },
    styles: getInteractivePillStyles(lineColor),
    ignoreEvent: false,
  });

  return figures;
};

function getInteractivePillStyles(lineColor: string) {
  return {
    style: "fill",
    color: "#FFFFFF",
    size: 10,
    family: "Arial",
    weight: "bold",
    backgroundColor: lineColor,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: 3,
    borderStyle: "solid",
    borderColor: lineColor,
    borderSize: 1,
  };
}

// Interactive SL/TP line overlay - based on trade line overlay but draggable
export const interactiveSlTpLineOverlay = {
  name: "interactiveSlTpLine",
  totalStep: 1,
  lock: false, // Allow dragging
  visible: true,
  zLevel: 10, // Higher z-level to ensure it's on top
  needDefaultPointFigure: false, // Hide default point circles
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: true, // Show price on Y axis
  mode: "normal" as const,
  modeSensitivity: 8,

  createPointFigures: (params: any) => {
    return createInteractiveSlTpLineFigures(params);
  },

  // Y axis price label - identyczna jak w statycznych liniach
  createYAxisFigures: (params: any) => {
    const { coordinates, overlay, yAxis } = params;

    if (coordinates.length < 1) return [];

    const lineData: InteractiveSlTpLineData = overlay.extendData;
    if (!lineData) return [];

    const lineColor = getLineColor(lineData);

    // Use actual Y coordinate to get current price (aktualizuje się podczas drag)
    const yCoord = coordinates[0].y;
    const currentPrice = yAxis.convertFromPixel(yCoord);
    const priceText = `${lineData.type} ${currentPrice.toFixed(5)}`;

    return [
      {
        key: "yAxisInteractiveSlTpLabel",
        type: "text",
        attrs: {
          x: 0,
          y: yCoord,
          text: priceText,
          align: "left",
          baseline: "middle",
        },
        styles: {
          style: "fill",
          color: "#FFFFFF",
          size: 10, // Zmienione z 11 na 10 jak w statycznych
          family: "Arial",
          weight: "bold",
          backgroundColor: lineColor,
          paddingLeft: 4, // Zmienione z 6 na 4 jak w statycznych
          paddingRight: 4, // Zmienione z 6 na 4 jak w statycznych
          paddingTop: 2, // Zmienione z 3 na 2 jak w statycznych
          paddingBottom: 2, // Zmienione z 3 na 2 jak w statycznych
          borderRadius: 2, // Zmienione z 3 na 2 jak w statycznych
          borderStyle: "solid",
          borderColor: lineColor,
          borderSize: 1,
        },
      },
    ];
  },

  // Handle drag operations by updating the point - ONLY VERTICAL MOVEMENT
  performEventPressedMove: (params: any) => {
    const { points, performPoint, performPointIndex, overlay } = params;

    if (performPointIndex >= 0 && performPoint && points[performPointIndex]) {
      // WAŻNE: Użyj oryginalnego timestamp z extendData (jeśli istnieje)
      // lub obecnego timestamp punktu
      const originalTimestamp =
        overlay?.extendData?.originalTimestamp ||
        overlay?.extendData?.openTime ||
        points[performPointIndex].timestamp;

      // Update TYLKO wartość Y, ZAWSZE zachowując oryginalny timestamp
      points[performPointIndex].value = performPoint.value;
      points[performPointIndex].timestamp = originalTimestamp; // ZAWSZE przywróć oryginalny timestamp
    }
  },

  onClick: (event: any) => {
    // Check if click was on the badge
    if (event && event.figureKey === "actionBadge") {
      // Find the overlay ID from the event context
      const overlay = event.overlay;
      if (overlay && overlay.id) {
        const callbacks = getOverlayCallbacks(overlay.id);
        const lineData = overlay.extendData;

        if (callbacks?.onBadgeClick && lineData) {
          // POPRAWKA: Pobierz aktualną cenę z punktu overlay'a zamiast z extendData
          const currentPrice =
            overlay.points && overlay.points[0]
              ? overlay.points[0].value
              : lineData.price;

          // Get current autoSlTpModify value from callback
          const currentAutoSlTpModify =
            callbacks?.getCurrentAutoSlTpModify?.() ??
            (lineData.autoSlTpModify || false);

          // If autoSlTpModify is enabled, handle automatically BUT still respect badge type
          if (currentAutoSlTpModify) {
            // NOWA LOGIKA: Nawet z auto-modify, sprawdź czy to X czy > badge
            const currentTradeValue = lineData.currentTradeValue || 0;
            const valuesAreEqual =
              Math.abs(currentPrice - currentTradeValue) < 0.00001;

            if (valuesAreEqual) {
              // Badge is "×" - remove action (nawet z auto-modify)
              if (callbacks?.onAutoModify) {
                callbacks.onAutoModify(lineData.type, 0); // Usuń z 0
              }
            } else {
              // Badge is ">" - modify action (normalny auto-modify)
              if (callbacks?.onAutoModify) {
                callbacks.onAutoModify(lineData.type, currentPrice);
              }
            }
            return true; // Consume the event
          }

          // NOWA LOGIKA: Porównaj z currentTradeValue zamiast originalPrice
          const currentTradeValue = lineData.currentTradeValue || 0;
          const valuesAreEqual =
            Math.abs(currentPrice - currentTradeValue) < 0.00001;

          if (valuesAreEqual) {
            // Badge is "×" - remove action
            callbacks.onBadgeClick(lineData.type, "remove", 0);
          } else {
            // Badge is ">" - modify action
            callbacks.onBadgeClick(lineData.type, "modify", currentPrice);
          }
          return true; // Consume the event
        }
      }
    }
    return false;
  },

  onMouseEnter: () => {
    // Match the unified `tradeLevelOverlay` cursor so users get the same
    // hover affordance for both committed and "creating new" lines.
    document.querySelectorAll("canvas").forEach((canvas) => {
      canvas.style.cursor = "pointer";
    });
    return false;
  },

  onMouseLeave: () => {
    document.querySelectorAll("canvas").forEach((canvas) => {
      canvas.style.removeProperty("cursor");
    });
    return false;
  },
};

// Function to register interactive overlay
export const registerInteractiveSlTpLineOverlay = () => {
  try {
    // Check if klinecharts is available
    if (!klinecharts) {
      return;
    }

    // Check if overlay is already registered
    const supportedOverlays = klinecharts.getSupportOverlays?.();

    if (supportedOverlays?.includes("interactiveSlTpLine")) {
      return;
    }

    klinecharts.registerOverlay(interactiveSlTpLineOverlay);
  } catch (error) {
    console.error("Failed to register interactive SL/TP line overlay:", error);
  }
};

// Helper function to create interactive SL/TP line overlay
export const createInteractiveSlTpLineOverlay = (
  chartInstance: any,
  lineData: InteractiveSlTpLineData,
  onPriceChange?: (type: "SL" | "TP", price: number) => void,
  onBadgeClick?: (
    type: "SL" | "TP",
    action: "remove" | "modify",
    price: number
  ) => void,
  onAutoModify?: (type: "SL" | "TP", price: number) => void,
  getCurrentAutoSlTpModify?: () => boolean
) => {
  if (!chartInstance) {
    return null;
  }

  // Anchor the overlay at the trade's open time so klinecharts gives us an
  // x-coordinate for the start of the line. Without `timestamp`, the
  // overlay would have no x-anchor and the visible line would span the full
  // pane width — breaking the partial-line look the rest of the chart uses.
  const overlayConfig = {
    name: "interactiveSlTpLine",
    points: [
      {
        timestamp: lineData.openTime,
        value: lineData.price,
      },
    ],
    extendData: {
      ...lineData,
    },
    // Simplified callback - no timestamp handling needed for horizontal line
    onPressedMoveEnd: ({ overlay }: { overlay: any }) => {
      if (overlay && overlay.points && overlay.points[0] && onPriceChange) {
        const newPrice = overlay.points[0].value;

        const callbacks = getOverlayCallbacks(overlay.id);

        // Drag-drop always commits the new level immediately.
        if (overlay.id && callbacks?.onAutoModify) {
          callbacks.onAutoModify(lineData.type, newPrice);
        }

        onPriceChange(lineData.type, newPrice);

        // Update overlay with new price
        try {
          chartInstance.overrideOverlay({
            id: overlay.id,
            extendData: {
              ...overlay.extendData,
              price: newPrice, // Update current price
            },
          });
        } catch (error) {
          console.error("Failed to update overlay after drag:", error);
        }
      }
      return false;
    },
  };

  const overlayId = chartInstance.createOverlay(overlayConfig);

  // Store callbacks for this overlay instance
  if (overlayId && (onBadgeClick || onAutoModify || getCurrentAutoSlTpModify)) {
    storeOverlayCallbacks(overlayId, {
      onBadgeClick,
      onAutoModify,
      getCurrentAutoSlTpModify,
    });
  }

  return overlayId;
};

// Helper function to clean up overlay
export const removeInteractiveSlTpLineOverlay = (
  chartInstance: any,
  overlayId: string
) => {
  if (!chartInstance || !overlayId) return;

  try {
    chartInstance.removeOverlay(overlayId);
    // Clean up stored callbacks
    removeOverlayCallbacks(overlayId);
  } catch (error) {
    console.error("Failed to remove interactive SL/TP overlay:", error);
  }
};
